import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createAd } from '../../api/videos';
import { uploadVideoFile } from '../../api/storage';
import { apiClient } from '../../services/api';

type CreativeType = 'text' | 'image' | 'video';

const splitToArray = (value: string): string[] =>
  value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const errorRecord = error as {
    message?: unknown;
    response?: {
      data?: {
        message?: unknown;
        error?: unknown;
      };
      status?: unknown;
    };
  };

  if (errorRecord.response?.status === 401) {
    return 'Please sign in before creating an ad.';
  }

  if (errorRecord.response?.status === 403) {
    return 'Your account does not have permission to create this ad.';
  }

  if (errorRecord.response?.status === 404) {
    return 'The upload or ad service is not available on the current backend URL.';
  }

  const responseMessage = errorRecord.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim()) {
    return responseMessage;
  }

  const responseError = errorRecord.response?.data?.error;
  if (typeof responseError === 'string' && responseError.trim()) {
    return responseError;
  }

  if (typeof errorRecord.message === 'string' && errorRecord.message.trim()) {
    return errorRecord.message;
  }

  return fallback;
};

const AdCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadedVideoKey, setUploadedVideoKey] = useState<string | null>(null);
  const [categories, setCategories] = useState('');
  const [tags, setTags] = useState('');
  const [landingUrl, setLandingUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [useBillboard, setUseBillboard] = useState(true);
  const [useSocial, setUseSocial] = useState(true);
  const [creativeType, setCreativeType] = useState<CreativeType>('image');

  /* ── AI Video generation state ──────────────────────────────────── */
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<{ videoUrl: string; model: string; elapsedMs: number } | null>(null);

  const handleAiGenerate = useCallback(async () => {
    const trimmedPrompt = aiPrompt.trim();
    if (!trimmedPrompt) return;
    setAiGenerating(true);
    setAiError(null);
    setAiResult(null);
    try {
      const res = await apiClient.post<{ videoUrl: string; model: string; elapsedMs: number }>(
        '/ai-video/generate',
        { prompt: trimmedPrompt },
      );
      setAiResult(res.data);
      setVideoUrl(res.data.videoUrl);
      setSuccess('AI video generated! It has been attached to this ad.');
    } catch (err: unknown) {
      const msg = getErrorMessage(err, 'AI video generation failed');
      setAiError(msg);
    } finally {
      setAiGenerating(false);
    }
  }, [aiPrompt]);

  const handleUploadVideo: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();
    if (!videoFile) {
      setError('Please choose a video file to upload.');
      return;
    }
    setError(null);
    setSuccess(null);
    setUploadingVideo(true);
    try {
      const result = await uploadVideoFile(videoFile);
      setUploadedVideoKey(result.key);
      setVideoUrl(result.url);
      setCreativeType('video');
      setSuccess('Video uploaded successfully. It will be used for this ad.');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to upload video'));
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedTitle = title.trim();
    const trimmedDescription = description.trim();
    const trimmedImageUrl = imageUrl.trim();
    const trimmedLandingUrl = landingUrl.trim();
    const trimmedVideoUrl = videoUrl.trim();

    if (!trimmedTitle || !trimmedDescription) {
      setError('Title and description are required.');
      return;
    }

    if (creativeType === 'image' && !trimmedImageUrl) {
      setError('Provide an image URL for an image ad, or switch this creative to text.');
      return;
    }

    if (creativeType === 'video' && !trimmedVideoUrl) {
      setError('Upload a video file or paste a video URL for a video ad.');
      return;
    }

    if (!useBillboard && !useSocial) {
      setError('Choose at least one channel (billboard or social/share).');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: trimmedTitle,
        description: trimmedDescription,
        creativeType,
        imageUrl: creativeType === 'image' ? trimmedImageUrl || undefined : undefined,
        videoUrl: creativeType === 'video' ? trimmedVideoUrl || undefined : undefined,
        landingUrl: trimmedLandingUrl || undefined,
        channels: [
          ...(useBillboard ? ['billboard'] : []),
          ...(useSocial ? ['social'] : []),
        ],
        categories: splitToArray(categories),
        tags: splitToArray(tags),
      };

      await createAd(payload);
      setSuccess('Ad created successfully! It will appear in your feed shortly.');
      setTitle('');
      setDescription('');
      setImageUrl('');
      setVideoUrl('');
      setVideoFile(null);
      setUploadedVideoKey(null);
      setCategories('');
      setTags('');
      setLandingUrl('');
      setUseBillboard(true);
      setUseSocial(true);
      setCreativeType('image');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to create ad'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mb-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm">
      <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Create a New Ad</h2>
      <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
        Choose exactly one ad type. Text ads use copy only, image ads use an image URL, and video
        ads use an uploaded or pasted video URL.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-3 text-sm">
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Creative type</label>
          <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-700 dark:text-slate-300">
            {(['text', 'image', 'video'] as const).map((typeOption) => {
              const isActive = creativeType === typeOption;

              return (
                <button
                  key={typeOption}
                  type="button"
                  onClick={() => setCreativeType(typeOption)}
                  className={`inline-flex items-center rounded-full border px-3 py-1 font-medium transition ${
                    isActive
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {typeOption === 'text'
                    ? 'Text ad'
                    : typeOption === 'image'
                      ? 'Image ad'
                      : 'Video ad'}
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Text ads only need title and description. Image ads require an image URL. Video ads
            require an uploaded or pasted video URL.
          </p>
        </div>

        {creativeType === 'image' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Image URL</label>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/your-image.jpg"
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            Required for image ads.
          </p>
        </div>
        )}

        {creativeType === 'video' && (
        <div>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Video</label>
          <div className="mt-1 space-y-2">
            <input
              type="file"
              accept="video/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                setVideoFile(file);
              }}
              className="block w-full text-xs text-slate-700 dark:text-slate-300 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1 file:text-xs file:font-medium file:text-slate-800 hover:file:bg-slate-200"
            />
            <button
              type="button"
              onClick={handleUploadVideo}
              disabled={uploadingVideo || !videoFile}
              className="inline-flex items-center rounded-md bg-slate-800 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-700 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {uploadingVideo ? 'Uploading…' : 'Upload video file'}
            </button>
          </div>

          {/* ── AI Video Generator ─────────────────────────────────── */}
          <div className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3">
            <p className="text-xs font-semibold text-indigo-900">Generate with AI</p>
            <p className="mt-0.5 text-[11px] text-indigo-700">
              Describe the video you&apos;d like and our AI will create a billboard-ready clip.
            </p>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="A cinematic billboard ad for a luxury car dealership at sunset..."
              rows={2}
              maxLength={2000}
              className="mt-2 w-full rounded-md border border-indigo-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <button
                type="button"
                onClick={handleAiGenerate}
                disabled={aiGenerating || !aiPrompt.trim()}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {aiGenerating ? 'Generating…' : '✨ Generate AI Video'}
              </button>
              <span className="text-[11px] text-indigo-600">{aiPrompt.length} / 2 000</span>
            </div>
            {aiError && <p className="mt-1.5 text-[11px] text-red-600">{aiError}</p>}
            {aiResult && (
              <div className="mt-2 space-y-1.5">
                <p className="text-[11px] text-emerald-700 font-medium">
                  Video generated in {(aiResult.elapsedMs / 1000).toFixed(1)}s via {aiResult.model}
                </p>
                <video
                  src={aiResult.videoUrl}
                  controls
                  className="w-full max-w-md rounded-md border border-slate-200 dark:border-slate-700"
                />
              </div>
            )}
          </div>

          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
            You can upload a video file, generate one with AI above, or paste a direct video URL below.
          </p>
          <input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://example.com/your-video.mp4"
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {videoUrl.trim() && (
            <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-900">
              <p className="font-medium">TELLURVISION hosted video URL</p>
              <input
                type="text"
                readOnly
                value={videoUrl}
                className="mt-1 w-full rounded border border-emerald-200 bg-white dark:bg-slate-900 px-2 py-1 font-mono text-[11px] text-slate-700 dark:text-slate-300"
              />
              <p className="mt-1 text-emerald-800">
                This URL will be saved with the ad and used for billboard playback.
              </p>
            </div>
          )}
          {uploadedVideoKey && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 break-all">
              Stored as object key: <span className="font-mono">{uploadedVideoKey}</span>
            </p>
          )}
        </div>
        )}

        {creativeType === 'text' && (
          <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-[11px] text-slate-600 dark:text-slate-400">
            This is a text ad. TELLURVISION will render it from your title and description without
            requiring an image or video asset.
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Categories (comma-separated)
            </label>
            <input
              type="text"
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              placeholder="Retail, Automotive"
              className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="Sale, Downtown, Evening"
              className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Business website or checkout URL (for QR scans)
            </label>
            <input
              type="url"
              value={landingUrl}
              onChange={(e) => setLandingUrl(e.target.value)}
              placeholder="https://example.com/your-store-or-offer"
              className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              When present, QR codes and special share links can send people directly to this
              page and track scans in analytics.
            </p>
          </div>
        </div>

        <div className="mt-2 grid gap-3 md:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">Where will this run?</label>
            <div className="mt-1 flex flex-col gap-1 text-xs text-slate-700 dark:text-slate-300">
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={useBillboard}
                  onChange={(e) => setUseBillboard(e.target.checked)}
                  className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />
                <span>TELLURVISION billboards</span>
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={useSocial}
                  onChange={(e) => setUseSocial(e.target.checked)}
                  className="h-3 w-3 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                />
                <span>Social / share links</span>
              </label>
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">AI creative checklist</label>
            <div className="mt-1 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-2 text-[11px] text-slate-700 dark:text-slate-300">
              <ul className="space-y-1 list-disc pl-4">
                {useBillboard && (
                  <li>
                    <span className="font-semibold">Billboard:</span>{' '}
                    {creativeType === 'video'
                      ? videoUrl.trim()
                        ? 'Video will play without sound in cars. Make sure your key message and offer appear as clear on-screen text.'
                        : 'Video ad selected. Upload a file or paste a direct video URL before creating the ad.'
                      : creativeType === 'image'
                        ? 'Static image will be used. Keep the headline short, high-contrast, and easy to read at a distance.'
                        : 'Text creative will be rendered from your title and description. Keep the headline short and the body copy minimal.'}
                  </li>
                )}
                {useSocial && (
                  <li>
                    <span className="font-semibold">Social:</span>{' '}
                    {creativeType === 'video'
                      ? videoUrl.trim()
                        ? 'Great for feeds and stories. Consider adding captions and a strong hook in the first 3 seconds.'
                        : 'Video ad selected, but the file still needs to be uploaded or the direct video URL pasted.'
                      : creativeType === 'image'
                        ? 'You can still share an image-based ad, but short video usually performs better on social.'
                        : 'Text-only ads can still work for social, but consider pairing them with a simple branded image later.'}
                  </li>
                )}
                {landingUrl.trim() && (
                  <li>
                    <span className="font-semibold">QR / clicks:</span> People who scan or click can be sent directly to{' '}
                    <span className="font-mono">{landingUrl.trim()}</span>. Make sure this page is ready for visitors.
                  </li>
                )}
                {!useBillboard && !useSocial && (
                  <li className="text-red-600">Select at least one channel so AI can evaluate your creative.</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {success && (
          <div className="space-y-2 text-xs">
            <p className="text-green-600">{success}</p>
            <button
              type="button"
              onClick={() => navigate('/payment')}
              className="inline-flex items-center rounded-md border border-blue-600 px-3 py-1 font-medium text-blue-600 hover:bg-blue-50"
            >
              Set budget & go to payments
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-400"
        >
          {submitting ? 'Creating...' : 'Create Ad'}
        </button>
      </form>
    </section>
  );
};

export default AdCreateForm;
